import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = { class: "text-h6 q-my-md" }
const _hoisted_4 = { class: "row items-center" }
const _hoisted_5 = { class: "text-h6 q-my-md" }

import { useDialogPluginComponent } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import SfxBtnIcon from "src/components/button/SfxBtnIcon.vue"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import SfxInput from "src/components/input/SfxInput.vue"
import SfxSeparator from "src/components/special/SfxSeparator.vue"
import { useConfigurationStore } from "src/stores/configurationStore"
import { useUserStore } from "src/stores/userStore"
import { Log } from "src/util/classes/log"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { getTranslatedEntityForPage } from "src/util/functions/entity"
import { SearchInColumn, TableColumnsConfig } from "src/util/types/components/sfxTable"
import { EntityPages } from "src/util/types/dataModels/entityDefinition"
import { UserCurrent } from "src/util/types/dataModels/user"
import { computed, onMounted, PropType, ref } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxDialogTableConfigurations',
  props: {
  visibleColumns: {
    type: Array as PropType<Array<string>>,
    required: true,
  },

  filterTerm: {
    type: String,
    required: true,
  },

  entity: {
    type: String as PropType<EntityPages>,
    required: true,
  },

  searchInColumn: {
    type: Object as PropType<SearchInColumn>,
  },
},
  emits: [...useDialogPluginComponent.emits],
  setup(__props) {

const props = __props



//endregion

//region Variables

const { t } = useI18n()
const configurationStore = useConfigurationStore()
const userStore = useUserStore()

const translatedEntity = computed(() => getTranslatedEntityForPage(props.entity))

const canSaveConfigText = computed(() =>
  t("sfxDialogTableConfigurations.can_save_config", {
    entity: translatedEntity.value,
  })
)

const configLimitReachedText = computed(() =>
  t("sfxDialogTableConfigurations.config_limit_reached", {
    entity: translatedEntity.value,
  })
)

const configExplanationText = t("sfxDialogTableConfigurations.config_explanation")

const btnLoadSelected = t("sfxButtons.btn_load_selected")
const btnLoadDefault = t("sfxButtons.btn_load_default")
const btnOverwriteSelected = t("sfxButtons.btn_overwrite_selected")
const btnDeleteSelected = t("sfxButtons.btn_delete_selected")

const loadConfigActive = ref(false)
const deleteConfigActive = ref(false)
const overwriteConfigActive = ref(false)

const actionBtnText = ref<string>()
const actionBtnDisable = ref<boolean>()
const newConfigName = ref<string>()
const selectedConfigIndex = ref<number>(-1)

const savedConfigs = computed<TableColumnsConfig[]>(
  () => configurationStore.getCurrentConfiguration.tableBookmarks[props.entity]
)

const currentTableColumnConfig = computed<TableColumnsConfig>(() => {
  return {
    configName: "",
    visibleColumns: props.visibleColumns,
    filterTerm: props.filterTerm,
    searchInColumn: props.searchInColumn,
    isFavourite: false,
  }
})

const hasCurrentConfig = computed<boolean>(() => allowNewConfigs.value)

const hasSavedConfigs = computed<boolean>(() => savedConfigs.value && savedConfigs.value.length > 0)

const allowNewConfigs = computed<boolean>(() => savedConfigs.value.length <= 7)

const showSeparator = computed<boolean>(() => hasCurrentConfig.value && hasSavedConfigs.value)

const dialogIntroText = computed(() =>
  (allowNewConfigs.value ? canSaveConfigText : configLimitReachedText).value.concat(
    configExplanationText
  )
)

const paddingBottom = computed(() => (hasSavedConfigs.value ? "q-pb-none" : ""))

const { dialogRef, onDialogOK, onDialogCancel } = useDialogPluginComponent()

//endregion

//region Lifecycle

onMounted(() => setActionBtnProperties())

//endregion

//region Functions

function setActionBtnProperties() {
  if (hasCurrentConfig.value) {
    actionBtnText.value = btnLoadSelected
    actionBtnDisable.value = true
  } else {
    actionBtnText.value = btnLoadDefault
    actionBtnDisable.value = false
  }
}

async function saveCurrentConfig(currentConfigName: string) {
  currentTableColumnConfig.value.configName = currentConfigName
  const arrayLength = configurationStore.tableBookmarks[props.entity].push(
    currentTableColumnConfig.value
  )

  const userData: Partial<UserCurrent> = {
    settings: { data: configurationStore.getCurrentConfiguration },
  }
  await userStore.uploadUserData(userData)

  // We just added the entity, so get the last index of the array
  onDialogOK(arrayLength - 1)
}

function getFavouriteIcon(index: number) {
  return savedConfigs.value[index]?.isFavourite ? Icons.FAVOURITE : Icons.NOT_FAVOURITE
}

async function updateFavourite(index: number) {
  const savedConfig = savedConfigs.value[index]

  if (savedConfig?.isFavourite) {
    savedConfig.isFavourite = false
  } else {
    const favouriteConfig = savedConfigs.value.find((config) => config.isFavourite)
    if (favouriteConfig) favouriteConfig.isFavourite = false
    savedConfig.isFavourite = true
  }

  const userData: Partial<UserCurrent> = {
    settings: { data: configurationStore.getCurrentConfiguration },
  }
  await userStore.uploadUserData(userData)
}

function loadSelected(index: number) {
  actionBtnText.value = btnLoadSelected
  actionBtnDisable.value = false

  loadConfigActive.value = true
  deleteConfigActive.value = false
  overwriteConfigActive.value = false

  selectedConfigIndex.value = index
}

async function deleteConfig(index: number) {
  actionBtnText.value = btnDeleteSelected
  actionBtnDisable.value = false

  deleteConfigActive.value = true
  loadConfigActive.value = false
  overwriteConfigActive.value = false

  selectedConfigIndex.value = index
}

function overwriteConfig(index: number) {
  actionBtnText.value = btnOverwriteSelected
  actionBtnDisable.value = false

  overwriteConfigActive.value = true
  loadConfigActive.value = false
  deleteConfigActive.value = false

  selectedConfigIndex.value = index
}

async function actionBtnOperations() {
  switch (actionBtnText.value) {
    case btnLoadDefault: {
      onDialogOK(-1)
      break
    }
    case btnLoadSelected: {
      onDialogOK(selectedConfigIndex.value)
      break
    }
    case btnDeleteSelected: {
      configurationStore.tableBookmarks[props.entity].splice(selectedConfigIndex.value, 1)

      const userData: Partial<UserCurrent> = {
        settings: { data: configurationStore.getCurrentConfiguration },
      }
      await userStore.uploadUserData(userData)

      deleteConfigActive.value = false
      setActionBtnProperties()
      break
    }
    case btnOverwriteSelected: {
      const selectedConfig = savedConfigs.value[selectedConfigIndex.value]
      currentTableColumnConfig.value.configName = selectedConfig.configName
      currentTableColumnConfig.value.isFavourite = selectedConfig.isFavourite

      configurationStore.tableBookmarks[props.entity][selectedConfigIndex.value] =
        currentTableColumnConfig.value

      const userData: Partial<UserCurrent> = {
        settings: { data: configurationStore.getCurrentConfiguration },
      }
      await userStore.uploadUserData(userData)

      overwriteConfigActive.value = false
      setActionBtnProperties()
      break
    }
    default:
      onDialogOK(-1)
      Log.error({ message: "TableConfigDialog action btn text is not set properly" })
  }
}

function addInputFieldBorder(index: number) {
  return loadConfigActive.value && selectedConfigIndex.value === index
    ? "sfx-table-config--selected"
    : ""
}

function deleteIconColor(index: number) {
  return deleteConfigActive.value && selectedConfigIndex.value === index ? "secondary" : "navy-base"
}

function saveIconColor(index: number) {
  return overwriteConfigActive.value && selectedConfigIndex.value === index
    ? "secondary"
    : "navy-base"
}

function resetSelections() {
  setActionBtnProperties()

  loadConfigActive.value = false
  deleteConfigActive.value = false
  overwriteConfigActive.value = false
}

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_card_section, { class: "text-h3 q-mb-sm" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("sfxDialogTableConfigurations.title")), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "text-body1" }, {
            default: _withCtx(() => [
              _createElementVNode("div", { innerHTML: dialogIntroText.value }, null, 8, _hoisted_1)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "text-body1" }, {
            default: _withCtx(() => [
              _createElementVNode("div", {
                innerHTML: _unref(t)('sfxDialogTableConfigurations.favorite_config')
              }, null, 8, _hoisted_2)
            ]),
            _: 1
          }),
          (hasCurrentConfig.value)
            ? (_openBlock(), _createBlock(_component_q_card_section, {
                key: 0,
                class: _normalizeClass(paddingBottom.value)
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)("sfxDialogTableConfigurations.section_save_current_config")), 1),
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(SfxInput, {
                      class: "col sfx-table-config--new-config q-px-lg",
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (resetSelections())),
                      modelValue: newConfigName.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newConfigName).value = $event)),
                      label: _ctx.$t('sfxDialogTableConfigurations.label_name'),
                      "hide-bottom-space": ""
                    }, null, 8, ["modelValue", "label"]),
                    _createVNode(SfxBtnIcon, {
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (saveCurrentConfig(newConfigName.value))),
                      disable: !newConfigName.value,
                      icon: _unref(Icons).SAVE,
                      padding: "xs",
                      color: "navy-base"
                    }, null, 8, ["disable", "icon"])
                  ])
                ]),
                _: 1
              }, 8, ["class"]))
            : _createCommentVNode("", true),
          (showSeparator.value)
            ? (_openBlock(), _createBlock(SfxSeparator, {
                key: 1,
                dimmed: ""
              }))
            : _createCommentVNode("", true),
          (hasSavedConfigs.value)
            ? (_openBlock(), _createBlock(_component_q_card_section, {
                key: 2,
                class: "q-py-none"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(t)("sfxDialogTableConfigurations.section_saved_configs")), 1),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(savedConfigs.value, (configuration, index) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: index,
                      class: "row items-center q-pb-md"
                    }, [
                      _createVNode(SfxBtnIcon, {
                        class: "q-mr-xs",
                        onClick: ($event: any) => (updateFavourite(index)),
                        icon: getFavouriteIcon(index),
                        padding: "xs",
                        color: "navy-base"
                      }, null, 8, ["onClick", "icon"]),
                      _createVNode(SfxInput, {
                        class: _normalizeClass(["col sfx-table-config", addInputFieldBorder(index)]),
                        onClick: ($event: any) => (loadSelected(index)),
                        modelValue: configuration.configName,
                        "onUpdate:modelValue": ($event: any) => ((configuration.configName) = $event),
                        label: _ctx.$t('sfxDialogTableConfigurations.label_name'),
                        readonly: "",
                        "hide-bottom-space": ""
                      }, null, 8, ["class", "onClick", "modelValue", "onUpdate:modelValue", "label"]),
                      _createVNode(SfxBtnIcon, {
                        class: "q-ml-xs",
                        onClick: ($event: any) => (deleteConfig(index)),
                        icon: _unref(Icons).DELETE,
                        padding: "xs",
                        color: deleteIconColor(index)
                      }, null, 8, ["onClick", "icon", "color"]),
                      _createVNode(SfxBtnIcon, {
                        onClick: ($event: any) => (overwriteConfig(index)),
                        icon: _unref(Icons).SAVE,
                        disable: !hasCurrentConfig.value,
                        padding: "xs",
                        color: saveIconColor(index)
                      }, null, 8, ["onClick", "icon", "disable", "color"])
                    ]))
                  }), 128))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_component_q_card_actions, null, {
            default: _withCtx(() => [
              _createVNode(SfxBtnOutline, {
                onClick: _unref(onDialogCancel),
                label: _unref(t)('sfxButtons.btn_close'),
                class: "col"
              }, null, 8, ["onClick", "label"]),
              _createVNode(SfxBtn, {
                onClick: actionBtnOperations,
                label: actionBtnText.value,
                disable: actionBtnDisable.value,
                class: "col"
              }, null, 8, ["label", "disable"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})